<template>
  <div class="flex flex-col gap-2">
    <p class="font-medium text-grayscale-70 flex">Phone number</p>
    <div class="flex">
      <Multiselect
        v-model="selectedCountry.code"
        track-by="value"
        :valueProp="'code'"
        :options="dialcodeOptions"
        :searchable="true"
        :close-on-select="true"
        :canClear="false"
        :canDeselect="false"
        :loading="isLoading"
        :resolve-on-load="true"
        ref="multiselect"
        class="multiselect-for-tel w-56 h-50px border border-grayscale-13"
        @open="isOpen = true"
        @close="isOpen = false"
        @change="onChange"
      >
        <template #singlelabel="{ value }">
          <div class="multiselect-single-label">
            <p>{{ value.label }} {{ value.value }}</p>
          </div>
        </template>

        <template #option="{ option }">
          <p v-if="option.label && option.value">
            {{ option.label }} {{ option.value }}
          </p>
        </template>
      </Multiselect>
      <input-field
        v-model:value="phone"
        name="phone"
        type="text"
        label-class="mb-2 font-14 text-manatee"
        full-container-class="w-available"
        input-class="h-50px border border-grayscale-13 rounded-r-4"
        :is-disabled="isLoading"
        v-model:error-message="phoneError"
        :styles-clear="true"
        pattern="^[0-9+\-.\(\)]*$"
        @input="validatePhone"
        @clearError="clearError"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import * as libphonenumber from "google-libphonenumber";
import type { SelectCountry } from "../../../types/location";
import Multiselect from "@vueform/multiselect";
import inputField from "@/components/layout/inputs/InputField.vue";

const props = defineProps({
  phone: {
    type: String,
    required: true,
  },
  errorMessage: {
    type: String,
  },
});

const emit = defineEmits(["phoneChanged", "update:errorMessage"]);

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

const isLoading = ref(false);
const isOpen = ref(false);

const phoneError = computed({
  get: () => props.errorMessage,
  set: (value) => emit("update:errorMessage", value),
});

const clearError = () => {
  phoneError.value = "";
};

const selectedCountry = ref<SelectCountry>({
  label: "🇺🇸",
  value: "+1",
  code: "US",
});
const dialcodeOptions = ref<SelectCountry[]>(
  constants.countries.map((country) => {
    return {
      label: country.flag,
      value: "+" + country.phoneCode,
      code: country.code,
    };
  }),
);
const phone = ref("");

onMounted(() => {
  if (props.phone) {
    const formatedPhone = phoneUtil.parse(props.phone);
    const code = ref(phoneUtil.getRegionCodeForNumber(formatedPhone));
    if (code.value) {
      selectedCountry.value = dialcodeOptions.value.filter(
        (country) => country.code === code.value,
      )[0];
      phone.value = props.phone.replace(selectedCountry.value.value, "");
    }
  }
});

watch(
  () => props.errorMessage,
  (newValue) => {
    phoneError.value = newValue;
  },
);

const onChange = (value: string | undefined) => {
  if (value) {
    selectedCountry.value = dialcodeOptions.value.filter(
      (country) => country.code === value,
    )[0];
    if (!validatePhone().isValid) {
      emit("phoneChanged", {
        phone: "",
        isValid: false,
      });
    } else {
      phoneError.value = "";
      emit("phoneChanged", {
        phone: validatePhone().phone,
        isValid: true,
      });
    }
  } else {
    phoneError.value = "";
    emit("phoneChanged", {
      phone: "",
      isValid: true,
    });
  }
};

const validatePhone = (): { phone: string; isValid: boolean } => {
  if (phone.value) {
    try {
      const dialcodePhone = selectedCountry.value.value + phone.value;
      const formatedPhone = phoneUtil.parse(dialcodePhone);

      emit("phoneChanged", {
        phone: dialcodePhone,
        isValid: phoneUtil.isPossibleNumber(formatedPhone),
      });

      return {
        phone: dialcodePhone,
        isValid: phoneUtil.isPossibleNumber(formatedPhone),
      };
    } catch (e) {
      emit("phoneChanged", {
        phone: "",
        isValid: false,
      });
      return {
        phone: "",
        isValid: false,
      };
    }
  } else {
    emit("phoneChanged", {
      phone: "",
      isValid: true,
    });
    return {
      phone: "",
      isValid: true,
    };
  }
};
</script>
